// src/modules/care-gaps-2/components/CareGapList.jsx
import React from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";

import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PhoneIcon from "@mui/icons-material/Phone";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import ReorderIcon from "@mui/icons-material/Reorder";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

// ===================== Helpers =====================

const getWorkflowChipColor = (code) => {
  switch (String(code || "").toUpperCase()) {
    case "NEW":
      return "default";
    case "CONTACT_ATTEMPTED":
      return "warning";
    case "CONTACTED":
      return "primary";
    case "SCHEDULED":
      return "success";
    case "EVIDENCE_UPLOADED":
      return "info";
    case "VERIFIED":
      return "success";
    case "CLOSED_BY_USER":
    case "EXEMPT":
      return "default";
    default:
      return "default";
  }
};

const getWorkflowChipLabel = (item) => {
  if (item.workflowStatus) return item.workflowStatus;

  switch (String(item.workflowStatusCode || "").toUpperCase()) {
    case "NEW":
      return "New - Not reviewed";
    case "CONTACT_ATTEMPTED":
      return "Contact attempted";
    case "CONTACTED":
      return "Contacted";
    case "SCHEDULED":
      return "Scheduled";
    case "EVIDENCE_UPLOADED":
      return "Evidence uploaded";
    case "VERIFIED":
      return "Verified";
    case "CLOSED_BY_USER":
      return "Closed by user";
    case "EXEMPT":
      return "Exempt / Not applicable";
    default:
      return item.workflowStatusCode || "";
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
};

const isClosedCareGap = (item) => {
  const source = String(item?.sourceStatusCode || "").toUpperCase();
  const wf = String(item?.workflowStatusCode || "").toUpperCase();

  // Source status
  if (source === "CLOSED") return true;

  // Workflow finales
  if (["VERIFIED", "CLOSED_BY_USER", "EXEMPT"].includes(wf)) return true;

  return false;
};

const isOpenCareGap = (item) => {
  const source = String(item?.sourceStatusCode || "").toUpperCase();
  return source === "OPEN";
};

// ===================== Component =====================

export default function CareGapList({
  items,
  onOpenDetail,
  onSchedule,
  onSubmitEvidence,
  onLogOutreach,
  onCareGapActions,
  onOpenObservations,
}) {
  if (!items || items.length === 0) {
    return (
      <Paper sx={{ p: 2, borderRadius: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No care gaps found with current filters.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Results
        </Typography>
      </Box>
      <Divider />

      {items.map((item) => {
        const workflowChipColor = getWorkflowChipColor(item.workflowStatusCode);
        const workflowChipLabel = getWorkflowChipLabel(item);

        const closed = isClosedCareGap(item);
        const open = isOpenCareGap(item);

        return (
          <Box key={item.id}>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
                transition: "background-color 120ms ease, opacity 120ms ease",
                bgcolor: closed ? "grey.50" : "transparent",
                opacity: closed ? 0.9 : 1,

                borderLeft: "5px solid",
                borderLeftColor: closed
                  ? "grey.900" // CLOSED
                  : open
                  ? "info.main" // OPEN
                  : "grey.300",

                "&:hover": {
                  bgcolor: closed ? "grey.100" : "grey.50",
                  cursor: "pointer",
                },
              }}
              onClick={() => onOpenDetail?.(item)}
            >
              {/* ===================== Left column ===================== */}
              <Box sx={{ flexGrow: 1, pr: 2, minWidth: 0 }}>
                {/* Title + tags */}
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800 }}
                    component="div"
                    noWrap
                  >
                    {item.careGapName}
                  </Typography>

                  {/* ✅ OPEN tag: azul con texto blanco */}
                  {open && !closed && (
                    <Chip
                      icon={<RadioButtonUncheckedIcon />}
                      label="OPEN"
                      size="small"
                      variant="filled"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        letterSpacing: 0.4,
                        bgcolor: "info.main",
                        color: "common.white",
                        "& .MuiChip-icon": { color: "common.white" },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* ✅ CLOSED tag: oscuro */}
                  {closed && (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="CLOSED"
                      size="small"
                      variant="filled"
                      sx={{
                        borderRadius: 999,
                        fontWeight: 900,
                        letterSpacing: 0.4,
                        bgcolor: "grey.900",
                        color: "common.white",
                        "& .MuiChip-icon": { color: "common.white" },
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* Workflow chip */}
                  {workflowChipLabel && (
                    <Chip
                      label={workflowChipLabel}
                      size="small"
                      color={workflowChipColor}
                      variant={closed ? "outlined" : "filled"}
                      sx={{
                        borderRadius: 999,
                        fontWeight: 700,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  )}

                  {/* ❌ Quitamos el chip repetido (sourceStatusCode),
                      porque ya lo estamos representando con OPEN/CLOSED */}
                </Stack>

                {/* Patient line */}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5, lineHeight: 1.35 }}
                >
                  Patient: <b>{item.patientFullName || item.patientName}</b> (CIP{" "}
                  {item.cipId}) &nbsp;&nbsp; PCP: {item.pcpName || "No PCP"}
                  &nbsp;&nbsp; Updated:{" "}
                  {formatDate(item.lastUpdated || item.createdDate)}
                </Typography>

                {/* Non-compliant history */}
                {item.historicallyNonCompliant && (
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ mt: 0.5 }}
                  >
                    <WarningAmberIcon
                      fontSize="small"
                      sx={{ color: "warning.main" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ color: "warning.main", fontWeight: 700 }}
                    >
                      Non-compliant history
                    </Typography>
                  </Stack>
                )}
              </Box>

              {/* ===================== Right column: actions ===================== */}
              <Stack
                direction="row"
                spacing={0.5}
                alignItems="center"
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip
                  title={closed ? "Closed care gap (actions disabled)" : "Schedule"}
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={closed}
                      onClick={() => onSchedule?.(item)}
                    >
                      <EventAvailableIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    closed ? "Closed care gap (actions disabled)" : "Submit to AVALITY"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={closed}
                      onClick={() => onSubmitEvidence?.(item)}
                    >
                      <UploadFileIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                <Tooltip
                  title={closed ? "Closed care gap (actions disabled)" : "Log outreach"}
                >
                  <span>
                    <IconButton
                      size="small"
                      disabled={closed}
                      onClick={() => onLogOutreach?.(item)}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>

                {onCareGapActions && (
                  <Tooltip
                    title={closed ? "Closed care gap (actions disabled)" : "Care Gap Actions"}
                  >
                    <span>
                      <IconButton
                        size="small"
                        disabled={closed}
                        onClick={() => onCareGapActions?.(item)}
                      >
                        <AssignmentTurnedInIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}

                {onOpenObservations && (
                  <Tooltip title="Patient observations">
                    <IconButton
                      size="small"
                      onClick={() => onOpenObservations?.(item)}
                    >
                      <ReorderIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>

            <Divider />
          </Box>
        );
      })}
    </Paper>
  );
}
